import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../lib/redux";
import {
  startGetAdSpendAdvertisersMeta,
  startGetAdSpendAllTime,
  startGetAdSpendDaily,
  startGetAdSpendMonthly,
  startGetAdSpendSeries,
} from "../actions/ownerAdSpend";
import {
  AD_SPEND_DAILY_SERIES_MAX_DAYS,
  AD_SPEND_DATE_PRESET,
  AD_SPEND_TOP_LIMIT,
  AD_SPEND_TRACKING_START_DATE,
  PLATFORMS_SUPPORTED,
} from "../utils/constants";
import { OWNER_AD_SPEND_VS_PREVIOUS_PERIOD } from "../i18n/keysTranslations";
import {
  buildChartData,
  buildKpis,
  buildMonthlySeries,
  buildPlatformShare,
  buildRangeReadPlan,
  buildTopAdvertiserRows,
  buildTopCountryRows,
  getChartGranularity,
  getPreviousPeriod,
  isBeforeTrackingStart,
  mergeRollups,
  resolvePresetRange,
  toDateKey,
  toDateKeyOrNull,
} from "../utils/adSpend";

import { LaunchError } from "../utils/errors";

/**
 * Dev-only mock for the owner ad spend page.
 * Create `.env.development.local` (gitignored) and restart `npm run dev`:
 *   VITE_OWNER_AD_SPEND_MOCK=full
 * Other values: `empty` (no data) and `error`.
 * The import() must stay inside this `import.meta.env.DEV` check. A folded
 * const is not enough: Vite still emits the fixture chunk.
 */
let mockSourcePromise = null;

const loadMockSource = async () => {
  if (!(import.meta.env.DEV && import.meta.env.VITE_OWNER_AD_SPEND_MOCK)) {
    return null;
  }
  if (!mockSourcePromise) {
    const scenario = import.meta.env.VITE_OWNER_AD_SPEND_MOCK;
    // Inline import is intentional so production builds drop the fixture.
    mockSourcePromise = import("../mocks/adSpend/adSpendMockSource.js").then(
      (module) => module.createAdSpendMockSource({ scenario })
    );
  }
  return mockSourcePromise;
};

const IS_MOCK = Boolean(
  import.meta.env.DEV && import.meta.env.VITE_OWNER_AD_SPEND_MOCK
);

const createRtdbReaders = (dispatch) => ({
  getAllTime: () => dispatch(startGetAdSpendAllTime()),
  getMonthly: ({ from, to }) => dispatch(startGetAdSpendMonthly({ from, to })),
  getDaily: ({ from, to }) => dispatch(startGetAdSpendDaily({ from, to })),
  getSeries: ({ granularity, from, to }) =>
    dispatch(startGetAdSpendSeries({ granularity, from, to })),
  getAdvertisersMeta: () => dispatch(startGetAdSpendAdvertisersMeta()),
});

const unwrap = (result) => {
  if (!result?.ok) {
    const error = new Error(result?.message || "ad-spend-read-failed");
    error.code = result?.code;
    throw error;
  }
  return result.data;
};

const mergeKeyedMaps = (results) =>
  results.reduce((merged, result) => ({ ...merged, ...unwrap(result) }), {});

const readMergedRange = async ({ plan, readers }) => {
  const jobs = plan.dailyRanges.map((dailyRange) =>
    readers.getDaily(dailyRange)
  );
  if (plan.monthRange) jobs.push(readers.getMonthly(plan.monthRange));
  const nodes = mergeKeyedMaps(await Promise.all(jobs));
  return mergeRollups({ rollups: Object.values(nodes) });
};

const readChartSeries = async ({ readers, granularity, range, plan }) => {
  if (granularity === "daily") {
    return unwrap(await readers.getSeries({ granularity, ...range })) || {};
  }

  const monthlyPromise = plan.monthRange
    ? readers.getSeries({ granularity: "monthly", ...plan.monthRange })
    : null;
  const dailyPromises = plan.dailyRanges.map((dailyRange) =>
    readers.getSeries({ granularity: "daily", ...dailyRange })
  );
  const [monthlyResult, ...dailyResults] = await Promise.all([
    monthlyPromise,
    ...dailyPromises,
  ]);
  return buildMonthlySeries({
    monthlySeries: monthlyResult ? unwrap(monthlyResult) : {},
    dailySeries: mergeKeyedMaps(dailyResults),
  });
};

const useOwnerAdSpend = ({ preset, customRange }) => {
  const dispatch = useDispatchApp();
  const advertisersMetaRef = useRef(null);
  const requestIDRef = useRef(0);
  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const customFrom = toDateKeyOrNull(customRange?.startDateTime);
  const customTo = toDateKeyOrNull(customRange?.endDateTime);

  const selectedRange = useMemo(
    () =>
      resolvePresetRange({
        preset,
        today: toDateKey(new Date()),
        customRange: { from: customFrom, to: customTo },
      }),
    [preset, customFrom, customTo]
  );
  const isCustomPending =
    preset === AD_SPEND_DATE_PRESET.custom && !selectedRange;

  useEffect(() => {
    const requestID = requestIDRef.current + 1;
    requestIDRef.current = requestID;
    let active = true;
    const isCurrent = () => active && requestIDRef.current === requestID;

    if (isCustomPending) {
      setIsLoading(false);
      return undefined;
    }

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const readers = IS_MOCK
          ? await loadMockSource()
          : createRtdbReaders(dispatch);
        if (!isCurrent()) return;

        const readAdvertisersMeta = async () => {
          if (advertisersMetaRef.current) return advertisersMetaRef.current;
          const data = unwrap(await readers.getAdvertisersMeta()) || {};
          advertisersMetaRef.current = data;
          return data;
        };

        if (!selectedRange) {
          const [allTimeResult, seriesResult, advertisersMeta] =
            await Promise.all([
              readers.getAllTime(),
              readers.getSeries({
                granularity: "monthly",
                from: null,
                to: null,
              }),
              readAdvertisersMeta(),
            ]);
          if (!isCurrent()) return;

          setSnapshot({
            range: null,
            granularity: "monthly",
            current: unwrap(allTimeResult),
            comparison: null,
            series: unwrap(seriesResult) || {},
            advertisersMeta,
            comparisonLabelKey: null,
          });
          return;
        }

        const plan = buildRangeReadPlan(selectedRange);
        const granularity = getChartGranularity({
          ...selectedRange,
          dailyMaxDays: AD_SPEND_DAILY_SERIES_MAX_DAYS,
        });
        const [current, previous, series, advertisersMeta] = await Promise.all([
          readMergedRange({ plan, readers }),
          readMergedRange({
            plan: buildRangeReadPlan(getPreviousPeriod(selectedRange)),
            readers,
          }),
          readChartSeries({ readers, granularity, range: selectedRange, plan }),
          readAdvertisersMeta(),
        ]);
        if (!isCurrent()) return;

        setSnapshot({
          range: selectedRange,
          granularity,
          current,
          comparison: { current, previous },
          series,
          advertisersMeta,
          comparisonLabelKey: OWNER_AD_SPEND_VS_PREVIOUS_PERIOD,
        });
      } catch (readError) {
        dispatch(
          LaunchError(readError, false, [], {
            functionName: "readAdvertisersMeta",
            capture: "exception",
          })
        );
        if (isCurrent()) setError(readError);
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [dispatch, isCustomPending, selectedRange]);

  const derived = useMemo(() => {
    const current = snapshot?.current ?? null;
    const comparison = snapshot?.comparison ?? null;
    return {
      chartData: buildChartData({
        series: snapshot?.series,
        granularity: snapshot?.granularity ?? "monthly",
        from: snapshot?.range?.from ?? null,
        to: snapshot?.range?.to ?? null,
        platforms: PLATFORMS_SUPPORTED,
      }),
      platformShare: buildPlatformShare({
        current,
        comparison,
        platforms: PLATFORMS_SUPPORTED,
      }),
      topAdvertisers: buildTopAdvertiserRows({
        current,
        advertisersMeta: snapshot?.advertisersMeta,
        limit: AD_SPEND_TOP_LIMIT,
      }),
      topCountries: buildTopCountryRows({
        current,
        limit: AD_SPEND_TOP_LIMIT,
      }),
      kpis: buildKpis({ current, comparison }),
    };
  }, [snapshot]);

  return {
    isLoading,
    error,
    isEmpty: !(Number(snapshot?.current?.totals?.spendUSD) > 0),
    isMock: IS_MOCK,
    isCustomPending,
    granularity: snapshot?.granularity ?? "monthly",
    ...derived,
    showAccuracyWarning:
      Boolean(snapshot) &&
      isBeforeTrackingStart({
        from: snapshot.range?.from ?? null,
        trackingStartDate: AD_SPEND_TRACKING_START_DATE,
      }),
    comparisonLabelKey: snapshot?.comparisonLabelKey ?? null,
  };
};

export default useOwnerAdSpend;
